import { useEffect, useState, useMemo, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Stack,
  Collapse,
  Alert,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import QuizIcon from '@mui/icons-material/Quiz';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RuleIcon from '@mui/icons-material/Rule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAppStore } from '../store/useAppStore';
import { getCredits, useCredits } from '../billing/credits';
import { getAiUsage } from '../ai/cloudRunner';
import CodexActivityIndicator from './CodexActivityIndicator';
import AccountMenu from './AccountMenu';
import BrandLogo from './BrandLogo';
import { isHostedWeb } from '../config/cloud';

const DRAWER_WIDTH = 248;

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
}

interface NavSection {
  section: string;
  items: NavItem[];
  /** Rendered collapsed by default (expert/advanced screens). */
  collapsed?: boolean;
}

/**
 * Navigation, grouped by workflow stage. Advanced/tuning screens live in a
 * collapsed "Advanced" section so the everyday path stays short; on the hosted
 * web portal the prompt-engineering screens are hidden entirely.
 */
function buildNav(hostedWeb: boolean): NavSection[] {
  const sections: NavSection[] = [
    {
      section: 'Prepare',
      items: [
        { label: 'Dashboard', to: '/', icon: <DashboardIcon /> },
        { label: 'Student Profile', to: '/profile', icon: <PersonIcon /> },
        { label: 'Dental Programs', to: '/programs', icon: <SchoolIcon /> },
        { label: 'Prep Calendar', to: '/plan', icon: <CalendarMonthIcon /> },
      ],
    },
    {
      section: 'Questions & Answers',
      items: [
        { label: 'Program Research', to: '/research', icon: <TravelExploreIcon /> },
        { label: 'Research Review', to: '/review', icon: <FactCheckIcon /> },
        { label: 'Question Bank', to: '/questions', icon: <QuizIcon /> },
        { label: 'Saved Questions', to: '/saved', icon: <BookmarkIcon /> },
        { label: 'Answer Builder', to: '/answers', icon: <EditNoteIcon /> },
        { label: 'Answer Library', to: '/answer-library', icon: <LibraryBooksIcon /> },
      ],
    },
    {
      section: 'Practice',
      items: [
        { label: 'Infinite Practice', to: '/practice', icon: <AllInclusiveIcon /> },
        { label: 'MMI Station Library', to: '/mmi', icon: <ViewCarouselIcon /> },
        { label: 'Mock Interview', to: '/mock', icon: <RecordVoiceOverIcon /> },
        { label: 'Interview Day Mode', to: '/interview-day', icon: <EmojiEventsIcon /> },
        { label: 'Red Flag Coach', to: '/red-flags', icon: <WarningAmberIcon /> },
      ],
    },
    {
      section: 'Insights',
      items: [
        { label: 'Progress Analytics', to: '/analytics', icon: <InsightsIcon /> },
        { label: 'Settings', to: '/settings', icon: <SettingsIcon /> },
      ],
    },
  ];
  const advanced: NavItem[] = [{ label: 'Score Calibration', to: '/calibration', icon: <RuleIcon /> }];
  if (!hostedWeb) advanced.push({ label: 'Prompts & Rubric', to: '/prompts', icon: <TuneIcon /> });
  sections.push({ section: 'Advanced', items: advanced, collapsed: true });
  return sections;
}

/** "AI ready / not configured" chip fed by the cheap `codex --version` probe.
 *  Hidden on the hosted web portal — cloud AI needs no setup, so a status chip
 *  would only raise questions. */
function AiStatusChip() {
  const aiStatus = useAppStore((s) => s.aiStatus);
  const navigate = useNavigate();
  if (isHostedWeb()) return null;
  if (aiStatus.state === 'checking' || aiStatus.state === 'unknown') {
    return <Chip size="small" icon={<CircularProgress size={12} />} label="AI: checking…" />;
  }
  if (aiStatus.state === 'ready') {
    return (
      <Tooltip title={aiStatus.detail}>
        <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="AI: Codex ready" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`${aiStatus.detail} — click to configure`}>
      <Chip
        size="small"
        color="warning"
        icon={<ErrorOutlineIcon />}
        label="AI: not configured"
        onClick={() => navigate('/settings')}
      />
    </Tooltip>
  );
}

/** Remaining monthly AI credits (hosted web only, where AI is metered). Click
 *  goes to Settings → Subscription for the full picture and upgrade options. */
function CreditsChip() {
  const credits = useCredits();
  const navigate = useNavigate();
  const hosted = isHostedWeb();
  useEffect(() => {
    // Populate the store once on load; afterwards every AI response keeps it fresh.
    if (hosted && getCredits().used === null) void getAiUsage().catch(() => {});
  }, [hosted]);
  if (!hosted || credits.used === null || credits.allowance === null) return null;
  const remaining = Math.max(0, credits.allowance - credits.used);
  const low = credits.allowance > 0 && remaining / credits.allowance <= 0.2;
  return (
    <Tooltip title={`${credits.used} of ${credits.allowance} monthly AI credits used — click to manage your plan`}>
      <Chip
        size="small"
        variant="outlined"
        color={remaining === 0 ? 'error' : low ? 'warning' : 'default'}
        icon={<AutoAwesomeIcon />}
        label={`${remaining} AI credit${remaining === 1 ? '' : 's'}`}
        onClick={() => navigate('/settings')}
      />
    </Tooltip>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const nav = useMemo(() => buildNav(isHostedWeb()), []);
  // Collapsed-by-default sections still auto-open when the current route lives
  // inside one, so the selected item is never invisible.
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const g of nav) {
      initial[g.section] =
        !g.collapsed || g.items.some((i) => i.to !== '/' && location.pathname.startsWith(i.to));
    }
    return initial;
  });
  return (
    <Box sx={{ overflow: 'auto', py: 1 }}>
      {nav.map((group, gi) => (
        <Box key={group.section}>
          <Stack
            direction="row"
            alignItems="center"
            onClick={() => setOpen((o) => ({ ...o, [group.section]: !o[group.section] }))}
            sx={{ px: 2.5, mt: gi === 0 ? 0.5 : 2, mb: 0.25, cursor: 'pointer', userSelect: 'none', color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
          >
            <Typography variant="overline" sx={{ display: 'block', fontSize: 10.5, letterSpacing: '0.1em', flexGrow: 1 }}>
              {group.section}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transition: 'transform 0.2s',
                transform: open[group.section] ? 'none' : 'rotate(-90deg)',
              }}
            />
          </Stack>
          <Collapse in={!!open[group.section]}>
            <List dense disablePadding>
              {group.items.map((item) => {
                const selected =
                  item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
                return (
                  <ListItemButton
                    key={item.to}
                    component={Link}
                    to={item.to}
                    selected={selected}
                    onClick={onNavigate}
                    sx={{
                      mx: 1,
                      borderRadius: 2.5,
                      py: 0.7,
                      position: 'relative',
                      color: 'text.secondary',
                      '& .MuiListItemIcon-root': { color: 'text.secondary' },
                      '& .MuiListItemText-primary': { fontSize: 13.5, fontWeight: 500 },
                      '&:hover': { color: 'text.primary' },
                      // Selected = quiet tinted pill + small accent bar, so the
                      // current place is obvious without a heavy block of navy
                      // dominating the rail.
                      '&.Mui-selected': {
                        bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.09),
                        color: 'primary.main',
                        '& .MuiListItemText-primary': { fontWeight: 600 },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 4,
                          top: '22%',
                          bottom: '22%',
                          width: 3,
                          borderRadius: 999,
                          bgcolor: 'primary.main',
                        },
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.22 : 0.14),
                      },
                      '&.Mui-selected .MuiListItemIcon-root': { color: 'primary.main' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, '& svg': { fontSize: 19 } }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
          {/* Whitespace, not dividers, separates groups — the rail stays calm. */}
        </Box>
      ))}
    </Box>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const toast = useAppStore((s) => s.toast);
  const clearToast = useAppStore((s) => s.clearToast);

  const theme = useTheme();
  // Below md the permanent sidebar would crush the content — collapse it into
  // a hamburger-triggered temporary drawer and give main the full width.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CodexActivityIndicator />
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: { xs: 1, sm: 1.5 }, minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
          {!isDesktop && (
            <IconButton edge="start" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <BrandLogo size={30} nameSx={{ fontSize: { xs: 16, sm: 19 } }} />
          </Box>
          {/* Status chips are a desktop nicety — hidden on phones to keep the bar clean. */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            <AiStatusChip />
            <CreditsChip />
          </Box>
          <AccountMenu />
        </Toolbar>
      </AppBar>

      {isDesktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderColor: 'divider' },
          }}
        >
          <Toolbar />
          <NavList />
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
          <NavList onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, sm: 2.5, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        {children}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={clearToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert onClose={clearToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
